import type { CSSProperties, PointerEvent } from 'react'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import IconButton from 'LuxKit/Button/IconButton'
import useRebrandLightSurfaceButtonVariant from 'hooks/useRebrandLightSurfaceButtonVariant'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import { DrawerSlideUpAnimation } from 'LuxKit/Modal/ModalSheet'
import { MODAL_TRANSITION_DURATION } from 'LuxKit/Modal/modalConstants'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import AIActionButton from 'tripPlanner/components/Buttons/AIActionButton'
import Group from 'components/utils/Group'
import { breakpointSizes, mediaQueryDown } from 'components/utils/breakpoint'
import noop from 'lib/function/noop'
import srOnly from 'styles/tools/sr-only'
import ChatMapOfferCard from './ChatMapOfferCard'
import ChatMapPlaceCard from './ChatMapPlaceCard'
import ChatMapOffersDrawerSkeleton from './ChatMapOffersDrawerSkeleton'
import type { ExploreOffer } from 'tripPlanner/api/explore/types'
import type { TripItineraryData } from 'tripPlanner/api/types'
import { drawerBodyEmptyStatePaddingStyles } from './drawerBodyLayout.styles'
import type { ChatExploreCategory } from 'tripPlanner/config/exploreCategories'
import type { ExploreItem } from 'tripPlanner/types/common'

export const OFFERS_DRAWER_WIDTH = 380
const MOBILE_OFFERS_DRAWER_HEIGHT = 'clamp(12rem, 27vh, 15rem)'
const MOBILE_OFFERS_DRAWER_MIN_HEIGHT = 192
const MOBILE_OFFERS_DRAWER_MAX_HEIGHT = 240
const MOBILE_OFFERS_DRAWER_SHORT_RATIO = 0.27
const MOBILE_OFFERS_DRAWER_MID_RATIO = 0.62
const MOBILE_OFFERS_DRAWER_FULL_OFFSET = 16
const MOBILE_MEDIA_QUERY = `(max-width: ${breakpointSizes.mobile.max}px)`
const DRAG_CLICK_THRESHOLD = 4

type MobileDrawerSnap = 'short' | 'mid' | 'full'
const MOBILE_DRAWER_SNAPS: ReadonlyArray<MobileDrawerSnap> = ['short', 'mid', 'full']

interface DragState {
  startY: number
  startHeight: number
}

interface DrawerStyle extends CSSProperties {
  '--chat-map-offers-drawer-height'?: string
}

interface DrawerWrapperProps {
  inert?: '' | undefined
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

function getShortHeight(viewportHeight: number) {
  return clamp(viewportHeight * MOBILE_OFFERS_DRAWER_SHORT_RATIO, MOBILE_OFFERS_DRAWER_MIN_HEIGHT, MOBILE_OFFERS_DRAWER_MAX_HEIGHT)
}

function getMobileSnapHeights(parentHeight: number) {
  const fullHeight = Math.max(getShortHeight(parentHeight), parentHeight - MOBILE_OFFERS_DRAWER_FULL_OFFSET)

  return {
    short: getShortHeight(parentHeight),
    mid: clamp(parentHeight * MOBILE_OFFERS_DRAWER_MID_RATIO, getShortHeight(parentHeight), fullHeight),
    full: fullHeight,
  } satisfies Record<MobileDrawerSnap, number>
}

function getClosestSnap(currentHeight: number, snapHeights: Record<MobileDrawerSnap, number>): MobileDrawerSnap {
  return MOBILE_DRAWER_SNAPS.reduce<MobileDrawerSnap>(
    (closestSnap, snap) => {
      const closestDistance = Math.abs(currentHeight - snapHeights[closestSnap])
      const nextDistance = Math.abs(currentHeight - snapHeights[snap])
      return nextDistance < closestDistance ? snap : closestSnap
    },
    'short',
  )
}

function getNextSnap(snap: MobileDrawerSnap): MobileDrawerSnap {
  if (snap === 'short') return 'mid'
  if (snap === 'mid') return 'full'
  return 'short'
}

const DrawerWrapper = styled.div<DrawerWrapperProps>`
  flex-shrink: 0;
  width: 0;

  &.open {
    width: ${rem(OFFERS_DRAWER_WIDTH)};
  }

  ${mediaQueryDown.mobile} {
    position: absolute;
    inset-inline: 0;
    bottom: 0;
    z-index: 7;
    width: auto;
    height: var(--chat-map-offers-drawer-height, ${MOBILE_OFFERS_DRAWER_HEIGHT});
    pointer-events: none;
    transition: height ${MODAL_TRANSITION_DURATION}ms ease-out;

    &.dragging {
      transition: none;
    }

    &.open {
      width: auto;
      pointer-events: auto;
    }
  }
`

const Panel = styled.div`
  --chat-map-offers-drawer-offset: 100%;

  width: ${rem(OFFERS_DRAWER_WIDTH)};
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-inline-start: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  transform: translateX(var(--chat-map-offers-drawer-offset));
  will-change: transform;
  transition: transform ${MODAL_TRANSITION_DURATION}ms ease-out;

  [dir="rtl"] & {
    --chat-map-offers-drawer-offset: -100%;
  }

  .open > & {
    transform: translateX(0);
  }

  ${mediaQueryDown.mobile} {
    width: 100%;
    height: 100%;
    border-inline-start: 0;
    border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    border-start-start-radius: ${rem(20)};
    border-start-end-radius: ${rem(20)};
    border-end-end-radius: 0;
    border-end-start-radius: 0;
    box-shadow: ${props => props.theme.shadow.flat.large};
    position: relative;
    transform: translateY(calc(100% + ${rem(16)}));
    animation-duration: ${MODAL_TRANSITION_DURATION}ms;
    animation-fill-mode: both;
    animation-timing-function: ease-out;

    .open > & {
      transform: translateY(0);
    }

    .open:not(.entered) > & {
      animation-name: ${DrawerSlideUpAnimation};
    }

    .dragging > & {
      animation: none;
    }
  }
`

const DragRegion = styled.div`
  display: contents;

  ${mediaQueryDown.mobile} {
    cursor: grab;
    display: block;
    touch-action: none;

    &:active {
      cursor: grabbing;
    }
  }
`

const DragHandle = styled.button.attrs({ type: 'button' })`
  display: none;

  ${mediaQueryDown.mobile} {
    appearance: none;
    background: transparent;
    border: 0;
    display: flex;
    flex-shrink: 0;
    justify-content: center;
    padding: ${rem(10)} 0 ${rem(6)};
    width: 100%;

    &::before {
      content: '';
      width: ${rem(48)};
      height: ${rem(4)};
      border-radius: ${props => props.theme.borderRadius.round};
      background-color: ${props => props.theme.palette.neutral.surface.lightForeground};
    }
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${rem(16)} ${rem(20)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  flex-shrink: 0;
  gap: ${rem(16)};

  /* The empty state reads as one block with the heading, so it drops the rule
     that separates the header from a scrolling list. */
  &.borderless {
    border-bottom: 0;
  }

  ${mediaQueryDown.mobile} {
    padding: 0 ${rem(20)} ${rem(12)};
  }
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  flex-shrink: 0;
`

/*
  The result count sits on the title's baseline rather than under it, so the
  header costs one line instead of two - worth the most on the mobile bottom
  sheet, where the drawer opens at ~27vh and every line comes off the list.
  It wraps to its own line only when the title alone fills the row.
*/
const HeaderContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: ${rem(8)};
  row-gap: ${rem(2)};
  flex: 1;
  min-width: 0;
`

const HeaderTitle = styled(Heading)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const HeaderResultCount = styled(BodyText)`
  white-space: nowrap;
`

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;

  ${mediaQueryDown.mobile} {
    overscroll-behavior: contain;
  }
`

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  padding: ${rem(16)};

  ${mediaQueryDown.mobile} {
    padding: ${rem(12)} ${rem(16)} ${rem(16)};
  }
`

const EmptyState = styled.div`
  padding: ${rem(4)} ${rem(20)} ${rem(20)};
`

/* A region that is only mounted when it already holds its text is not reliably announced. */
const LiveRegion = styled.div`
  ${srOnly}
`

const PartialNotice = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(8)};
  padding-inline: ${rem(16)};
  padding-block: ${rem(8)};
`

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  ${drawerBodyEmptyStatePaddingStyles}
  text-align: center;
  gap: ${rem(16)};
`

/**
 * The drawer lists either LE offers (Hotels, Experiences) or Google Places
 * results (every other explore category), depending on the selected category's
 * source.
 */
export type ChatMapDrawerResults =
  | { kind: 'offers'; offers: Array<ExploreOffer> }
  | { kind: 'places'; places: Array<ExploreItem> }

interface Props {
  isOpen: boolean
  onClose: () => void
  category: ChatExploreCategory
  results: ChatMapDrawerResults
  isLoading: boolean
  isError: boolean
  /** One source failed: the list is short rather than wrong, so it shows with a notice. */
  isPartial?: boolean
  onRetry: () => void
  itineraryData?: TripItineraryData
  onSendMessage: (message: string) => void
  onViewOffer: (data: { offerId: string; offerType?: string }) => void
  onViewPlace: (place: ExploreItem) => void
  /** Primes the chat input with the current category as context */
  onAskAi: () => void
  highlightedMarkerId?: string
  onCardHover?: (markerId: string | undefined) => void
}

function ChatMapOffersDrawer({
  isOpen,
  onClose,
  category,
  results,
  isLoading,
  isError,
  isPartial,
  onRetry,
  itineraryData,
  onSendMessage,
  onViewOffer,
  onViewPlace,
  onAskAi,
  highlightedMarkerId,
  onCardHover = noop,
}: Props) {
  const drawerWrapperRef = useRef<HTMLDivElement>(null)
  const dragStateRef = useRef<DragState | undefined>(undefined)
  const dragHeightRef = useRef<number | undefined>(undefined)
  const didDragRef = useRef(false)
  const [mobileSnap, setMobileSnap] = useState<MobileDrawerSnap>('short')
  const [dragHeight, setDragHeight] = useState<number | undefined>(undefined)
  const [isDragging, setIsDragging] = useState(false)
  const [hasEntered, setHasEntered] = useState(false)
  const [parentHeight, setParentHeight] = useState(MOBILE_OFFERS_DRAWER_MAX_HEIGHT)

  const isMobileDrawer = useCallback(() => {
    return typeof window !== 'undefined' && window.matchMedia(MOBILE_MEDIA_QUERY).matches
  }, [])

  const getCurrentParentHeight = useCallback(() => {
    const parentHeight = drawerWrapperRef.current?.parentElement?.clientHeight
    if (parentHeight && parentHeight > 0) {
      return parentHeight
    }

    return typeof window !== 'undefined' ? window.innerHeight : MOBILE_OFFERS_DRAWER_MAX_HEIGHT
  }, [])

  const updateParentHeight = useCallback(() => {
    setParentHeight(getCurrentParentHeight())
  }, [getCurrentParentHeight])

  const getSnapHeights = useCallback(() => getMobileSnapHeights(parentHeight), [parentHeight])

  const snapTo = useCallback((snap: MobileDrawerSnap) => {
    setMobileSnap(snap)
    setDragHeight(undefined)
  }, [])

  useEffect(() => {
    if (!isOpen || !isMobileDrawer()) return

    updateParentHeight()

    const parentElement = drawerWrapperRef.current?.parentElement
    const resizeObserver = parentElement && typeof ResizeObserver !== 'undefined' ?
        new ResizeObserver(updateParentHeight) :
      undefined

    if (resizeObserver && parentElement) {
      resizeObserver.observe(parentElement)
    }
    window.addEventListener('resize', updateParentHeight)

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener('resize', updateParentHeight)
    }
  }, [isMobileDrawer, isOpen, updateParentHeight])

  const handleDragStart = useCallback((event: PointerEvent<HTMLElement>) => {
    if (!isOpen || !isMobileDrawer()) return

    dragStateRef.current = {
      startY: event.clientY,
      startHeight: drawerWrapperRef.current?.getBoundingClientRect().height ?? getSnapHeights()[mobileSnap],
    }
    dragHeightRef.current = undefined
    didDragRef.current = false
    setIsDragging(true)
  }, [getSnapHeights, isMobileDrawer, isOpen, mobileSnap])

  useEffect(() => {
    if (!isDragging) return

    const handlePointerMove = (event: globalThis.PointerEvent) => {
      const dragState = dragStateRef.current
      if (!dragState) return

      event.preventDefault()
      const snapHeights = getSnapHeights()
      const nextHeight = clamp(
        dragState.startHeight + dragState.startY - event.clientY,
        snapHeights.short,
        snapHeights.full,
      )

      didDragRef.current = Math.abs(event.clientY - dragState.startY) > DRAG_CLICK_THRESHOLD
      dragHeightRef.current = nextHeight
      setDragHeight(nextHeight)
    }

    const handlePointerEnd = () => {
      const snapHeights = getSnapHeights()
      const currentHeight = dragHeightRef.current ?? drawerWrapperRef.current?.getBoundingClientRect().height ?? snapHeights.short

      snapTo(getClosestSnap(currentHeight, snapHeights))
      dragStateRef.current = undefined
      dragHeightRef.current = undefined
      setIsDragging(false)
      window.requestAnimationFrame(() => {
        didDragRef.current = false
      })
    }

    document.addEventListener('pointermove', handlePointerMove)
    document.addEventListener('pointerup', handlePointerEnd)
    document.addEventListener('pointercancel', handlePointerEnd)

    return () => {
      document.removeEventListener('pointermove', handlePointerMove)
      document.removeEventListener('pointerup', handlePointerEnd)
      document.removeEventListener('pointercancel', handlePointerEnd)
    }
  }, [getSnapHeights, isDragging, snapTo])

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  useEffect(() => {
    if (!isOpen) {
      snapTo('short')
      setIsDragging(false)
      setHasEntered(false)
      dragStateRef.current = undefined
      dragHeightRef.current = undefined
    }
  }, [isOpen, snapTo])

  useEffect(() => {
    if (!isOpen) return

    const timeout = window.setTimeout(() => setHasEntered(true), MODAL_TRANSITION_DURATION)

    return () => window.clearTimeout(timeout)
  }, [isOpen])

  const handleDragHandleClick = useCallback(() => {
    if (didDragRef.current) {
      didDragRef.current = false
      return
    }

    snapTo(getNextSnap(mobileSnap))
  }, [mobileSnap, snapTo])

  // Header buttons sit inside the mobile drag region, so their pointer events
  // must not start a drag.
  const handleActionPointerDown = useCallback((event: PointerEvent<HTMLElement>) => {
    event.stopPropagation()
  }, [])

  const drawerHeight = dragHeight ?? (isOpen ? getSnapHeights()[mobileSnap] : undefined)
  const drawerStyle = useMemo<DrawerStyle | undefined>(() => {
    if (drawerHeight === undefined) return undefined

    return {
      '--chat-map-offers-drawer-height': rem(drawerHeight),
    }
  }, [drawerHeight])

  const intl = useIntl()
  const lightSurfaceButtonVariant = useRebrandLightSurfaceButtonVariant()

  const resultCount = results.kind === 'offers' ? results.offers.length : results.places.length
  const hasResults = resultCount > 0

  const resultsHeading = intl.formatMessage(category.labelMessage)

  const closePanelLabel = intl.formatMessage({
    id: 'ksE-HAW1PsIC7L4s6cLD',
    defaultMessage: 'Close panel',
    description: 'Aria-label for the close button on the map offers drawer',
  })

  const askAiLabel = intl.formatMessage({
    id: '8y-TWRLSiTNaLRDXx-2-',
    defaultMessage: 'Ask AI',
    description: 'Button label for asking AI about selected rows in the trip planner list quick actions toolbar',
  })

  const resizePanelLabel = intl.formatMessage({
    id: 'Zz3bmkDQXBG920b77Mto',
    defaultMessage: 'Resize map results panel',
    description: 'Aria-label for the drag handle used to resize the mobile map offers drawer',
  })

  const errorMessage = intl.formatMessage({
    id: 'EOTJFxW5DolMGZYAGBjT',
    defaultMessage: 'Something went wrong loading results.',
    description: 'Error message shown when map drawer results fail to load',
  })

  const partialMessage = intl.formatMessage({
    id: '_W6hntcIEiS-mZJm8QDf',
    defaultMessage: 'Some places couldn’t be loaded, so this list may be short.',
    description: 'Notice shown above the trip planner map results shelf when one of the two place sources failed and the list is incomplete',
  })

  const tryAgainLabel = intl.formatMessage({
    id: '_84xgIWalzdd0QZkvLTp',
    defaultMessage: 'Try again',
    description: 'Button label to retry loading map drawer offers after an error',
  })

  const resultsCountLabel = intl.formatMessage(
    {
      id: '3-GCnO1r8Qlb4W2MVOlO',
      defaultMessage: '{count, plural, one {# result} other {# results}}',
      description: 'Results count label in the map offers drawer header',
    },
    { count: resultCount },
  )

  const emptyStateHeading = intl.formatMessage({
    id: 'bPTZbUtVNm3KZcEYqYb6',
    defaultMessage: 'No results found',
    description: 'Empty state heading when no results are found in the current map area',
  })

  const emptyStateMessage = intl.formatMessage({
    id: 'bIeRMW_W6xe6gvv8xEwd',
    defaultMessage: 'To see more results, try panning or zooming the map.',
    description: 'Empty state hint when no results are found in the current map area',
  })

  // Same precedence as the shelf renders in: the failure, then the list it produced.
  const noticeAnnouncement = useMemo(() => {
    if (isLoading) return ''
    if (isError) return errorMessage
    if (!hasResults && !isPartial) return emptyStateHeading
    if (isPartial) return partialMessage

    return ''
  }, [emptyStateHeading, errorMessage, hasResults, isError, isLoading, isPartial, partialMessage])

  return (
    <DrawerWrapper
      ref={drawerWrapperRef}
      className={clsx({ open: isOpen, dragging: isDragging, entered: hasEntered })}
      aria-hidden={!isOpen}
      inert={!isOpen ? '' : undefined}
      data-testid="chat-map-offers-drawer"
      data-mobile-snap={mobileSnap}
      style={drawerStyle}
    >
      <Panel data-testid="chat-map-offers-drawer-panel">
        <DragRegion data-testid="chat-map-offers-drawer-drag-region">
          <DragHandle
            aria-label={resizePanelLabel}
            onPointerDown={handleDragStart}
            onClick={handleDragHandleClick}
            data-testid="chat-map-offers-drawer-drag-handle"
          />
          <Header
            onPointerDown={handleDragStart}
            className={clsx({ borderless: !isLoading && !isError && !hasResults })}
            data-testid="chat-map-offers-drawer-header"
          >
            <HeaderContent>
              <HeaderTitle variant="heading5">{resultsHeading}</HeaderTitle>
              {!isLoading && !isError && hasResults && (
                <HeaderResultCount variant="small" colour="light-foreground-alt">
                  {resultsCountLabel}
                </HeaderResultCount>
              )}
            </HeaderContent>
            <HeaderActions>
              <AIActionButton
                onClick={onAskAi}
                onPointerDown={handleActionPointerDown}
              >
                {askAiLabel}
              </AIActionButton>
              <IconButton
                kind="tertiary"
                variant={lightSurfaceButtonVariant}
                size="medium"
                onClick={onClose}
                onPointerDown={handleActionPointerDown}
                aria-label={closePanelLabel}
              >
                <LineTimesIcon />
              </IconButton>
            </HeaderActions>
          </Header>
        </DragRegion>
        <Body data-testid="chat-map-offers-drawer-body">
          <LiveRegion aria-live="polite" aria-atomic="true">{noticeAnnouncement}</LiveRegion>
          {isLoading && <ChatMapOffersDrawerSkeleton />}
          {!isLoading && isError && (
            <ErrorState>
              <Group direction="vertical" gap={8} horizontalAlign="center">
                <BodyText variant="medium" colour="light-foreground-alt">
                  {errorMessage}
                </BodyText>
                <TextButton
                  kind="secondary"
                  size="small"
                  onClick={onRetry}
                >
                  {tryAgainLabel}
                </TextButton>
              </Group>
            </ErrorState>
          )}
          {!isLoading && isPartial && (
            <PartialNotice>
              <BodyText variant="small" colour="light-foreground-alt">
                {partialMessage}
              </BodyText>
              <TextButton kind="tertiary" size="small" onClick={onRetry}>
                {tryAgainLabel}
              </TextButton>
            </PartialNotice>
          )}
          {!isLoading && !isError && !hasResults && (
            <EmptyState data-testid="chat-map-offers-drawer-empty-state">
              <Group direction="vertical" gap={4}>
                <BodyText variant="medium" weight="bold">
                  {emptyStateHeading}
                </BodyText>
                <BodyText variant="medium" colour="light-foreground-alt">
                  {emptyStateMessage}
                </BodyText>
              </Group>
            </EmptyState>
          )}
          {!isLoading && !isError && hasResults && (
            <CardList>
              {results.kind === 'offers' && results.offers.map((offer) => (
                <ChatMapOfferCard
                  key={offer.id}
                  offer={offer}
                  itineraryData={itineraryData}
                  onSendMessage={onSendMessage}
                  onViewOffer={onViewOffer}
                  onHover={onCardHover}
                  isHighlighted={highlightedMarkerId === offer.id}
                />
              ))}
              {results.kind === 'places' && results.places.map((place) => (
                <ChatMapPlaceCard
                  key={place.id}
                  place={place}
                  onViewPlace={onViewPlace}
                  onHover={onCardHover}
                  isHighlighted={highlightedMarkerId === place.id}
                />
              ))}
            </CardList>
          )}
        </Body>
      </Panel>
    </DrawerWrapper>
  )
}

export default memo(ChatMapOffersDrawer)
